import { handleScreenSizes, isSmallScreen } from '../util/window-resizing';

const expandIconClass = 'fa-chevron-down';
const collapseIconClass = 'fa-chevron-up';

interface PairOfButtons {
  tabButton: HTMLElement,
  accordionButton: HTMLElement | null,
}

let headerSize: number;
function getHeaderSize(): number {
  if (!headerSize) {
    const utilityBarHeight = document.querySelector('.id7-utility-bar').getBoundingClientRect().height;
    const logoHeight = document.querySelector('.id7-logo-column').getBoundingClientRect().height;
    headerSize = document.querySelector(".id7-page-header").getBoundingClientRect().height - utilityBarHeight - logoHeight + 10;
  }
  return headerSize;
}

function hide(buttonPair: PairOfButtons, panel: HTMLElement) {
  const { tabButton, accordionButton } = buttonPair;

  tabButton.classList.remove('active');
  tabButton.setAttribute('aria-selected', 'false');
  tabButton.setAttribute('tabindex', '-1');

  if (accordionButton) {
    accordionButton.setAttribute('aria-expanded', 'false');
    accordionButton.title = "Expand";
    const icon = accordionButton.querySelector('i');
    if (icon) {
      icon.classList.remove(collapseIconClass);
      icon.classList.add(expandIconClass);
    }
  }

  panel.classList.remove('active');
  panel.setAttribute('aria-hidden', 'true');
}

function hideAll(buttonPairs: PairOfButtons[], panelElements: NodeListOf<Element>) {
  buttonPairs.forEach((buttonPair, index) => {
    hide(buttonPair, panelElements[index] as HTMLElement);
  });
}

function show(buttonPair: PairOfButtons, panel: HTMLElement, scroll: boolean) {
  const { tabButton, accordionButton } = buttonPair;

  tabButton.classList.add('active');
  tabButton.setAttribute('aria-selected', 'true');
  tabButton.removeAttribute('tabindex');

  if (accordionButton) {
    accordionButton.setAttribute('aria-expanded', 'true');
    accordionButton.title = 'Collapse';
    const icon = accordionButton.querySelector('i');
    if (icon) {
      icon.classList.remove(expandIconClass);
      icon.classList.add(collapseIconClass);
    }
  }

  panel.classList.add('active');
  panel.setAttribute('aria-hidden', 'false');

  const top = panel.getBoundingClientRect().top;
  if (scroll && (top < 0 || top > window.innerHeight)) {
    window.scroll(window.scrollX, window.scrollY + top - getHeaderSize());
  }
}

function showIndex(index: number, buttonPairs: PairOfButtons[], panelElements: NodeListOf<Element>, scroll: boolean) {
  hideAll(buttonPairs, panelElements);
  show(buttonPairs[index], panelElements[index] as HTMLElement, scroll);
}

function toggle(buttonPair: PairOfButtons, panel: HTMLElement) {
  if (panel.classList.contains('active')) {
    hide(buttonPair, panel);
  } else {
    show(buttonPair, panel, true);
  }
}

export function init(element: ParentNode) {
  const tabComponent = element.querySelector('.tabs-component--tablist') as HTMLElement;
  if (tabComponent.dataset.initialised === 'true') { return; }
  const tabElements = tabComponent.querySelectorAll(':scope > button');

  const panels = element.querySelector('.tabs-component--panels');
  const panelElements = panels.querySelectorAll(':scope > section');
  const hash = window.location.hash.replace('#', '');
  const hashToIndexes: {[key: string]: number[]} = {};

  const isAccordion = (element as HTMLElement).classList.contains('tabs-accordion');
  const accordionAllowedExpanded = (element as HTMLElement).dataset.accordionAllowedExpanded ?? 'one';

  const buttonPairs: PairOfButtons[] = Array.from(tabElements).map((tabButton: HTMLElement) => {
    return {
      tabButton,
      accordionButton: panels.querySelector(`button[aria-controls="${tabButton.getAttribute('aria-controls')}"]`),
    }
  });

  let currentIndex = 0;
  const maxIndex = tabElements.length - 1;

  function showBasedOnSettings(buttonPair: PairOfButtons, panel: HTMLElement, currentlyOpen: boolean) {
    if (isAccordion || isSmallScreen()) {
      if (accordionAllowedExpanded === 'one' && (isAccordion || !isSmallScreen())) {
        hideAll(buttonPairs, panelElements);
        if (!currentlyOpen) {
          show(buttonPair, panel, true);
        }
      } else {
        toggle(buttonPair, panel);
      }
    } else {
      hideAll(buttonPairs, panelElements);
      show(buttonPair, panel, true);
    }
  }

  function checkHashAndShow() {
    const updatedHash = window.location.hash.replace('#', '');
    if (hashToIndexes[updatedHash] !== undefined) {
      hashToIndexes[updatedHash].forEach((index) => {
        showIndex(index, buttonPairs, panelElements, true);
      });
    }
  }

  function addIndexToHash(hash: string, index: number) {
    if (hashToIndexes[hash] === undefined) {
      hashToIndexes[hash] = [index];
    } else {
      hashToIndexes[hash].push(index);
    }
  }

  buttonPairs.forEach((buttonPair: PairOfButtons, tabIndex) => {
    const { tabButton, accordionButton } = buttonPair;
    const panel = panelElements[tabIndex] as HTMLElement;

    addIndexToHash(tabButton.id, tabIndex);

    // Add any ids contained within the panel to the hashToIndex map so that we open anything containing the hash
    panel.querySelectorAll('[id]').forEach((element) => {
      addIndexToHash(element.id, tabIndex);
    });

    if (tabButton.id === hash) {
      currentIndex = tabIndex;
    }

    [tabButton, accordionButton].forEach((button) => {
      if (button) {
        button.addEventListener('click', () => {
          currentIndex = tabIndex;
          const currentlyOpen = panel.classList.contains('active');
          showBasedOnSettings(buttonPair, panel, currentlyOpen);
        });
      }
    });

    tabButton.addEventListener('keydown', (event) => {
      switch (event.key) {
        case 'Up':
        case 'Left':
        case 'ArrowUp':
        case 'ArrowLeft':
          event.preventDefault();
          currentIndex = currentIndex > 0 ? currentIndex - 1 : maxIndex;
          showIndex(currentIndex, buttonPairs, panelElements, true);
          break;
        case 'Down':
        case 'Right':
        case 'ArrowDown':
        case 'ArrowRight':
          event.preventDefault();
          currentIndex = currentIndex < maxIndex ? currentIndex + 1 : 0;
          showIndex(currentIndex, buttonPairs, panelElements, true);
          break;
        case 'Home':
          event.preventDefault();
          currentIndex = 0;
          showIndex(currentIndex, buttonPairs, panelElements, true);
          break;
        case 'End':
          event.preventDefault();
          currentIndex = maxIndex;
          showIndex(currentIndex, buttonPairs, panelElements, true);
          break;
      }
    });
  });

  handleScreenSizes({
    onChangeToSmall: () => {
      if (!isAccordion) {
        hideAll(buttonPairs, panelElements);
      }
    },
    onChangeToLarge: () => {
      if (!isAccordion) {
        // We need to revert to having just one tab open. Let's go with the first active button or just the first
        // button if none are active.
        const activePair = buttonPairs.find((_) => _.accordionButton.getAttribute('aria-expanded') === 'true') ?? buttonPairs[0];
        const activePanel = Array.from(panelElements).find((_) => _.classList.contains('active')) ?? panelElements[0];
        showBasedOnSettings(activePair, activePanel as HTMLElement, activePanel.classList.contains('active'));
      }
    }
  });

  window.addEventListener('hashchange', () => {
    checkHashAndShow();
  });

  checkHashAndShow();

  if (!isAccordion && isSmallScreen()) {
    hideAll(buttonPairs, panelElements);
  }

  tabComponent.dataset.initialised = 'true';
}
